import { z } from 'zod';

import {
  createGlobalApiDetailsBaseSchema,
  createPayerSchema,
  FlowStatusSchema,
  PurposeSchema,
} from '@data/global-api-common';

export const PaymentInitiationFlowStatusSchema = FlowStatusSchema;

export const AwaitingReasonsSchema = z.object({
  AWAITING_DECOUPLED_AUTHORISATION: z.literal(
    'AWAITING_DECOUPLED_AUTHORISATION'
  ),
  AWAITING_REDIRECT_URL: z.literal('AWAITING_REDIRECT_URL'),
  AWAITING_USER_REDIRECT: z.literal('AWAITING_USER_REDIRECT'),
});

const AllowedScaMethodsSchema = z.object({
  CHIP_OTP: z.literal('CHIP_OTP'),
  PHOTO_OTP: z.literal('PHOTO_OTP'),
  PUSH_OTP: z.literal('PUSH_OTP'),
  SMS_OTP: z.literal('SMS_OTP'),
  REDIRECT: z.literal('REDIRECT'),
});

export const AllowedRedirectTypeSchema = z.object({
  APP: z.literal('APP'),
  WEB: z.literal('WEB'),
});

export const RequiredInputTypesSchema = z.object({
  ACCOUNT: z.literal('ACCOUNT'),
  ACCOUNT_NUMBER: z.literal('ACCOUNT_NUMBER'),
  BRANCH_CODE: z.literal('BRANCH_CODE'),
  IBAN: z.literal('IBAN'),
  INSTITUTION_ID: z.literal('INSTITUTION_ID'),
  PSU_ID: z.literal('PSU_ID'),
  PSU_IP_ADDRESS: z.literal('PSU_IP_ADDRESS'),
  PSU_ACCOUNT_NUMBER: z.literal('PSU_ACCOUNT_NUMBER'),
  SCA_CODE: z.literal('SCA_CODE'),
  SCA_PASSWORD: z.literal('SCA_PASSWORD'),
  SCA_METHOD: z.literal('SCA_METHOD'),
  SCA_USERNAME: z.literal('SCA_USERNAME'),
  SORT_CODE: z.literal('SORT_CODE'),
});

export const StatusesSchema = z.object({
  ABANDONED_BY_USER: z.literal('ABANDONED_BY_USER'),
  ACCOUNT_SELECTION: z.literal('ACCOUNT_SELECTION'),
  ADDITIONAL_AUTHORIZATION_REQUIRED: z.literal(
    'ADDITIONAL_AUTHORIZATION_REQUIRED'
  ),
  APPROVED_BY_RISK: z.literal('APPROVED_BY_RISK'),
  AUTHORISED_BY_USER: z.literal('AUTHORISED_BY_USER'),
  AWAITING_CHECKOUT_AUTHORISATION: z.literal('AWAITING_CHECKOUT_AUTHORISATION'),
  BANK_REDIRECT: z.literal('BANK_REDIRECT'),
  CANCELLED_BY_USER: z.literal('CANCELLED_BY_USER'),
  COMPLETED: z.literal('COMPLETED'),
  DELAYED_AT_BANK: z.literal('DELAYED_AT_BANK'),
  ERROR_AT_BANK: z.literal('ERROR_AT_BANK'),
  FAILED: z.literal('FAILED'),
  NEW_PAYMENT: z.literal('NEW_PAYMENT'),
  NOT_RECEIVED: z.literal('NOT_RECEIVED'),
  // PAYOUT_INITIATED: z.literal('PAYOUT_INITIATED'),
  PROVIDER_COMMUNICATION_ERROR: z.literal('PROVIDER_COMMUNICATION_ERROR'),
  RECEIVED: z.literal('RECEIVED'),
  REFUSED_BY_BANK: z.literal('REFUSED_BY_BANK'),
  REFUSED_BY_RISK: z.literal('REFUSED_BY_RISK'),
  REQUIRED_REDEEM: z.literal('REQUIRED_REDEEM'),
  SETTLED: z.literal('SETTLED'),
  UNKNOWN: z.literal('UNKNOWN'),
});

export const PaymentSucceedStatusesSchema = StatusesSchema.pick({
  COMPLETED: true,
  DELAYED_AT_BANK: true,
  RECEIVED: true,
  SETTLED: true,
  NOT_RECEIVED: true,
});

export const PaymentPendingStatusesSchema = StatusesSchema.pick({
  ADDITIONAL_AUTHORIZATION_REQUIRED: true,
  AUTHORISED_BY_USER: true,
  AWAITING_CHECKOUT_AUTHORISATION: true,
});

export const PaymentOngoingStatusesSchema = StatusesSchema.pick({
  ACCOUNT_SELECTION: true,
  APPROVED_BY_RISK: true,
  BANK_REDIRECT: true,
  NEW_PAYMENT: true,
  AWAITING_CHECKOUT_AUTHORISATION: true,
});

export const PaymentFailedStatusesSchema = StatusesSchema.pick({
  ABANDONED_BY_USER: true,
  CANCELLED_BY_USER: true,
  ERROR_AT_BANK: true,
  FAILED: true,
  REFUSED_BY_BANK: true,
  REFUSED_BY_RISK: true,
  PROVIDER_COMMUNICATION_ERROR: true,
  UNKNOWN: true,
});

export const PaymentFinalStatusesSchema = z.union([
  PaymentSucceedStatusesSchema.keyof(),
  PaymentFailedStatusesSchema.keyof(),
]);

export const ProvidersSchema = z.object({
  Neonomics: z.literal('Neonomics'),
  PowerGrid: z.literal('PowerGrid'),
  Token: z.literal('Token'),
  Volt: z.literal('Volt'),
  VoltMockProvider: z.literal('VoltMockProvider'),
  Yapily: z.literal('Yapily'),
});

const OpenBankingTypesSchema = z.object({
  ...PurposeSchema.shape,
  PERSON_TO_PERSON: z.literal('PERSON_TO_PERSON'),
});

export const ScaAllowedValuesSchema = z.object({
  description: z.string().nullable(),
  name: AllowedScaMethodsSchema.keyof(),
  value: z.string(),
});

export const ScaMethodRequiredInputSchema = z.object({
  allowedValues: z.array(ScaAllowedValuesSchema),
  name: RequiredInputTypesSchema.shape.SCA_METHOD,
  propertyPath: z.string(),
});

export const ScaMethodRequiredInputWithChallengeSchema = z.object({
  name: RequiredInputTypesSchema.shape.SCA_CODE,
  propertyPath: z.string(),
  challengeData: z
    .object({
      data: z.string(),
      type: z.string(),
    })
    .strict(),
});

const AccountSelectionAllowedValuesSchema = z.object({
  description: z.string().nullable(),
  name: z.string(),
  value: z.string(),
});

export const AccountSelectionRequiredInputSchema = z.object({
  allowedValues: z.array(AccountSelectionAllowedValuesSchema),
  name: RequiredInputTypesSchema.shape.ACCOUNT,
  propertyPath: z.string(),
});

export const RedirectTypeAllowedValuesSchema = z.object({
  description: z.string().nullable(),
  name: AllowedRedirectTypeSchema.keyof(),
  value: z.string(),
});

export const RedirectTypeRequiredInputSchema = z.object({
  allowedValues: z.array(RedirectTypeAllowedValuesSchema),
  name: z.literal('REDIRECT_TYPE'),
  propertyPath: z.string(),
});

export const AwaitingForUserRedirectDetailsSchema = z.object({
  reason: AwaitingReasonsSchema.shape.AWAITING_USER_REDIRECT,
  redirect: z
    .object({
      directUrl: z.string(),
      url: z.string(),
    })
    .strict(),
});

export const AwaitingForUserInstantRedirectDetailsSchema = z.object({
  reason: AwaitingReasonsSchema.shape.AWAITING_USER_REDIRECT,
  redirect: z.object({
    directUrl: z.string(),
    url: z.string(),
    instant: z.literal(true),
  }),
});

export const ProcessingDetailsSchema = z.union([
  AwaitingForUserRedirectDetailsSchema,
  AwaitingForUserInstantRedirectDetailsSchema,
  z.object({
    reason: z.enum([
      AwaitingReasonsSchema.shape.AWAITING_REDIRECT_URL.value,
      AwaitingReasonsSchema.shape.AWAITING_DECOUPLED_AUTHORISATION.value,
    ]),
  }),
]);

export const ProcessingSchema = z.object({
  details: ProcessingDetailsSchema.optional(),
  status: PaymentInitiationFlowStatusSchema.shape.PROCESSING,
});

const RequiredInputSchema = z.union([
  ScaMethodRequiredInputSchema,
  ScaMethodRequiredInputWithChallengeSchema,
  RedirectTypeRequiredInputSchema,
  AccountSelectionRequiredInputSchema,
  z.object({
    name: RequiredInputTypesSchema.keyof(),
    propertyPath: z.string(),
  }),
]);

export const WaitingForInputSchema = z.object({
  requiredInput: z.array(RequiredInputSchema),
  status: PaymentInitiationFlowStatusSchema.shape.WAITING_FOR_INPUT,
});

const PaymentInitiationFlowSchema = z.union([
  WaitingForInputSchema,
  ProcessingSchema,
  z.object({
    status: z.enum([
      PaymentInitiationFlowStatusSchema.shape.FINISHED.value,
      PaymentInitiationFlowStatusSchema.shape.ABORTED.value,
      PaymentInitiationFlowStatusSchema.shape.EXCEPTION.value,
    ]),
  }),
]);

const OpenBankingCommonSchema = z.object({
  institutionId: z.string().uuid().nullable(),
  provider: ProvidersSchema.keyof().nullable(),
  type: OpenBankingTypesSchema.keyof(),
  validityPeriod: z.number(),
});

const OpenBankingEUSchema = z.intersection(
  OpenBankingCommonSchema,
  z.object({
    accountIdentifiers: z.object({
      accountNumber: z.string().optional(),
      iban: z.string().optional(),
      branchCode: z.string().optional(),
    }),
  })
);

const OpenBankingUKSchema = z.intersection(
  OpenBankingCommonSchema,
  z.object({
    accountIdentifiers: z.object({
      accountNumber: z.string().optional(),
      iban: z.string().optional(),
      sortCode: z.string().optional(),
    }),
  })
);

const BeneficiaryEUSchema = z.object({
  accountIdentifiers: z.object({
    iban: z.string().optional(),
    swiftBic: z.string().optional(),
  }),
  name: z.string().nullable(),
});

const BeneficiaryUKSchema = z.object({
  accountIdentifiers: z.object({
    accountNumber: z.string().optional(),
    iban: z.string().optional(),
    sortCode: z.string().optional(),
    swiftBic: z.string().optional(),
  }),
  name: z.string().nullable(),
});

const DisplayInfoItemSchema = z.object({
  key: z.string(),
  value: z.string(),
});

export const PaymentDetailsWithRequiredScaCodeWithChallengeDataSchema =
  z.object({
    paymentInitiationFlow: z.object({
      requiredInput: z.array(ScaMethodRequiredInputWithChallengeSchema),
      status: PaymentInitiationFlowStatusSchema.shape.WAITING_FOR_INPUT,
    }),
  });

export const PaymentDetailsWithRequiredAccountSelectionSchema = z.object({
  paymentInitiationFlow: z.object({
    requiredInput: z.array(AccountSelectionRequiredInputSchema),
    status: PaymentInitiationFlowStatusSchema.shape.WAITING_FOR_INPUT,
  }),
});

const PaymentPayerSchema = createPayerSchema(
  z.object({
    iban: z.string().optional(),
    accountNumber: z.string().optional(),
    branchCode: z.string().optional(),
  })
);

const CommonPaymentDetailsSchema = createGlobalApiDetailsBaseSchema(
  PaymentPayerSchema
).extend({
  amount: z.number(),
  createdAt: z.string(),
  displayInfo: z.array(DisplayInfoItemSchema),
  paymentInitiationFlow: PaymentInitiationFlowSchema,
  paymentReference: z.string(),
  sender: z
    .object({
      accountIdentifiers: z.object({
        accountNumber: z.string().optional(),
        iban: z.string().optional(),
        sortCode: z.string().optional(),
        swiftBic: z.string().optional(),
      }),
      name: z.string().nullable(),
    })
    .nullable(),
  status: StatusesSchema.keyof(),
  transferType: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

const PaymentDetailsEUSchema = z.object({
  beneficiary: BeneficiaryEUSchema,
  openBankingEU: OpenBankingEUSchema,
  paymentSystem: z.literal('OPEN_BANKING_EU'),
});

const PaymentDetailsUKSchema = z.object({
  beneficiary: BeneficiaryUKSchema,
  openBankingUK: OpenBankingUKSchema,
  paymentSystem: z.literal('OPEN_BANKING_UK'),
});

export const GlobalApiPaymentsDetailsSchema = z.intersection(
  z.union([PaymentDetailsEUSchema, PaymentDetailsUKSchema]),
  CommonPaymentDetailsSchema
);
